import { useEffect, useState } from "react";
import { format } from "date-fns";
import { get } from "../../../../../api";
import { GET_EXERCISE_BY_PASSID } from "../../../../../api/apiRoutes";
import { Exercise } from "../../../../../models/Exercise";
import { Ticket } from "../../../../../models/Ticket";
import useFacility from "../../../../../../utils/hooks/useFacility";
import getId from "../../../../../../utils/getId";

export type PassEligibility = "eligible" | "not-eligible" | "unknown";

/**
 * Dla każdego karnetu pyta serwer o zajęcia dostępne tym karnetem w dniu wskazanych zajęć.
 * Te zajęcia są na liście → pasuje. Listy nie ma ich → nie pasuje. Błąd zapytania → nie wiadomo.
 */
export default function usePassEligibility(passes: Ticket[], exercise: Exercise | null) {
  const facilityIds = useFacility();
  const [eligibility, setEligibility] = useState<Record<string, PassEligibility>>({});
  const [checkedFor, setCheckedFor] = useState("");

  const passesKey = passes.map((pass) => getId(pass)).join("|");
  const exerciseId = exercise?.objectId;
  const exerciseStart = exercise?.startTime;
  const currentKey = `${exerciseId}|${passesKey}`;

  useEffect(() => {
    if (!exerciseId || !exerciseStart || passes.length === 0) {
      setEligibility({});
      setCheckedFor(currentKey);
      return;
    }

    if (!facilityIds) return;

    let cancelled = false;
    setEligibility({});
    setCheckedFor("");
    const date = format(exerciseStart, "yyyy-MM-dd");

    Promise.all(
      passes.map(async (pass): Promise<[string, PassEligibility]> => {
        const key = getId(pass);
        try {
          const { result, isError } = await get(GET_EXERCISE_BY_PASSID(facilityIds, date, date, key));
          if (isError || !Array.isArray(result)) return [key, "unknown"];
          const matches = result.some((item: Exercise) => String(item.objectId) === String(exerciseId));
          return [key, matches ? "eligible" : "not-eligible"];
        } catch {
          return [key, "unknown"];
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setEligibility(Object.fromEntries(entries));
      setCheckedFor(currentKey);
    });

    return () => {
      cancelled = true;
    };
    // passes jest zsynchronizowane z passesKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passesKey, exerciseId, exerciseStart, facilityIds]);

  const checking = passes.length > 0 && !!exerciseId && checkedFor !== currentKey;

  return { checking, eligibility };
}
